import {find} from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router';

import {sessionHistoryApi} from 'api';
import {Icon, Loader} from 'components';
import {useApiData} from 'hooks';
import {SessionFile} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {ErrorPage} from 'pages/Common';
import {UrlService} from 'services';

const T_KEYS = TRANSLATION_KEYS.USER.SESSION_FILE_REDIRECT;

export const SessionFileRedirect: React.FC = () => {
    const {t} = useTranslation();
    const [downloading, setDownloading] = useState(false);
    const [downloaded, setDownloaded] = useState(false);
    const [downloadError, setDownloadError] = useState();
    const {sessionId, fileParams: fileParamsEncoded} = useParams<{sessionId: string, fileParams: string}>();

    const fileParams = useMemo(() => UrlService.getJsonUriValueBase64(fileParamsEncoded), [fileParamsEncoded]);
    const fileId = useMemo(() => fileParams?.fileId, [fileParams]);

    const {send: getSessionHistory, data: sessions, error, loading} = useApiData(sessionHistoryApi.getSessionHistory);

    const session = useMemo(() => sessions?.items?.[0], [sessions]);
    const file = useMemo(() => find<SessionFile>(session?.files, {id: fileId}), [session, fileId]);

    const abortController = useMemo(() => new AbortController(), []);

    const errorMessage = useMemo(() => {
        if (!session) {
            return t(T_KEYS.MESSAGE.SESSION_NOT_FOUND);
        } else if (!file) {
            return t(T_KEYS.MESSAGE.FILE_NOT_FOUND);
        } else if (!file?.link && !file?.accessibleStorage) {
            return t(T_KEYS.MESSAGE.FILE_LINK_NOT_FOUND);
        } else if (error || downloadError) {
            return t(T_KEYS.MESSAGE.UNEXPECTED_ERROR);
        }
    }, [t, session, file, error, downloadError]);

    const downloadFile = useCallback(() => {
        setDownloading(true);
        sessionHistoryApi.downloadExternalAfterSessionFile(sessionId, file?.name, {signal: abortController.signal})
            .then(() => setDownloaded(true))
            .catch(setDownloadError).finally(() => setDownloading(false));
    }, [abortController, file?.name, sessionId]);

    useEffect(() => {
        getSessionHistory({sessionId}, {} as any);
    }, [sessionId, fileParams, getSessionHistory]);

    useEffect(() => () => {
        abortController.abort();
    }, [abortController]);

    useEffect(() => {
        if (file?.link) {
            window.location.href = file.link;
        } else if (file?.accessibleStorage && !downloading && !downloaded) {
            downloadFile();
        }
    }, [file, downloading, downloaded, downloadFile]);

    if (downloading) {
        return <Loader svgClassName='tw-max-w-12 tw-max-h-12' />;
    } else if (downloaded) {
        // TODO: make better UI in 3.2.0 with some words
        return (
            <div className='tw-h-full tw-w-full tw-flex-center'>
                <Icon name='check-circle' color='success' size='6xl' />
            </div>
        );
    } else if (!loading && errorMessage) {
        return <ErrorPage message={errorMessage} showOpenDefaultPageButton />;
    }

    return null;
};
